<script>
import dayjs from 'dayjs';
import { scaleBand, scaleLinear } from 'd3-scale';
import { ticks } from 'd3-array';
import IconGhost from '@/components/icons/Ghost';

const HEIGHT = 300;
const padding = {
  top: 10,
  right: 10,
  bottom: 20,
  left: 20,
};

const formatDate = d => dayjs(d).format('DD.MM.YY');

export default {
  props: {
    mapColors: {
      type: Object,
      required: true,
    },
    projects: {
      type: Array,
      required: true,
    },
    dateRange: {
      type: Array,
      required: true,
    },
  },
  data () {
    return {
      width: 0,
    };
  },
  computed: {
    chartWidth () {
      return this.width - padding.left - padding.right;
    },
    chartHeight () {
      return HEIGHT - padding.top - padding.bottom;
    },

    maxHours () {
      return Math.max(
        ...this.dateRange.map(date => (
          this.projects.reduce((acc, project) => (
            acc + project.hoursByDate[date]
          ), 0)
        )),
      );
    },

    xScale () {
      return scaleBand()
        .domain(this.dateRange)
        .paddingInner(0.1)
        .paddingOuter(0.5)
        .range([0, this.chartWidth])
        .round(true);
    },
    heightScale () {
      return scaleLinear()
        .domain([0, this.maxHours])
        .range([0, this.chartHeight])
        .nice();
    },

    xTicks () {
      const { $style, xScale, dateRange, chartHeight } = this;
      const bandwidth = xScale.bandwidth();

      return (
        <g class={[$style.axis, $style.x]}>
          {ticks(0, dateRange.length - 1, 5).map(tick => (
            <text
              key={dateRange[tick]}
              x={xScale(dateRange[tick]) + (bandwidth / 2)}
              y={chartHeight}
              dy="1.25em"
            >
              {formatDate(dateRange[tick])}
            </text>
          ))}
        </g>
      );
    },
    yTicks () {
      const { $style, heightScale, chartWidth, chartHeight } = this;

      return (
        <g class={[$style.axis, $style.y]}>
          {heightScale.ticks(5).map(tick => (
            <g
              key={tick}
              transform={`translate(0, ${chartHeight - heightScale(tick)})`}
            >
              <text
                x="0"
                y="0"
                dx="-.25em"
                dy=".35em"
              >
                {tick}
              </text>
              <line
                x1="0"
                x2={chartWidth}
                y1="0"
                y2="0"
              />
            </g>
          ))}
        </g>
      );
    },
  },
  mounted () {
    const { width } = this.$el.getBoundingClientRect();

    this.width = width;
  },
  render () {
    const {
      $style,
      width,
      chartHeight,
      projects,
      xScale,
      heightScale,
    } = this;

    if (this.isFetching) {
      return (
        <div style={{ minHeight: `${HEIGHT}px` }}>
          <u-spinner />
        </div>
      );
    }

    return (
      <div style={{ minHeight: `${HEIGHT}px` }}>
        {this.projects.length === 0 ? (
          <div class={$style.empty}>
            <span class={$style.emptyIcon}>
              <IconGhost />
            </span>
            <span>
              Ничего не найдено
            </span>
          </div>
        ) : (
          <svg
            viewBox={`0 0 ${width} ${HEIGHT}`}
            width={width}
            height={HEIGHT}
          >
            <g transform={`translate(${padding.left}, ${padding.top})`}>
              {this.yTicks}
              {this.xTicks}
              {this.dateRange.map(date => (
                <g
                  key={date}
                  transform={`translate(${xScale(date)}, 0)`}
                >
                  {projects.map((project, i) => {
                    const prevValue = projects
                      .slice(0, i)
                      .reduce((acc, d) => acc + d.hoursByDate[date], 0);
                    const value = project.hoursByDate[date];
                    const dy = heightScale(prevValue);
                    const height = heightScale(value);

                    return (
                      <rect
                        key={project.id}
                        x="0"
                        y={chartHeight - (dy + height)}
                        width={xScale.bandwidth()}
                        height={height}
                        fill={this.mapColors[project.id]}
                      >
                        <title>
                          {`${formatDate(date)}\n${project.name}: ${value}`}
                        </title>
                      </rect>
                    );
                  })}
                </g>
              ))}
            </g>
          </svg>
        )}
      </div>
    );
  },
};
</script>

<style lang="scss" module>
@import '@/theme/include.scss';

.empty {
  align-items: center;
  color: $color-gray;
  display: flex;

  &Icon {
    color: $color-gray-lighter;
    font-size: 4em;
    margin-right: .5em;
  }
}

.axis {
  & line {
    stroke: $color-gray;
  }

  & text {
    font-size: .75em;
  }

  &.y text {
    text-anchor: end;
  }

  &.x text {
    text-anchor: middle;
  }
}

.chart {
  stroke-width: 2;
}
</style>
